// Diagnostics counters + close-event logger for the Phase 1 robustness
// pass on `deepgramStreamingEngine.ts`. The engine maintains a single
// `DeepgramDiagnostics` record and exposes it via `getDiagnostics()` so
// `LeadDeviceAsrDiagnosticsPanel` (platform_dev only) can poll without
// reaching into private state. See docs/planning/deepgram_streaming_robustness.md
// §3 Phase 1 bullets 3 + 4.

import type { DeepgramAudioPath } from "./deepgramPcmPort";

export type DeepgramDiagnostics = {
  /** ms timestamp when the most recent `start()` call kicked off. Null before first start. */
  sessionStartedAtMs: number | null;
  /** ms timestamp of the most recent `new WebSocket(...)` call (initial OR reconnect). */
  lastConnectAttemptAtMs: number | null;
  /** ms timestamp of the most recent successful `ws.onopen`. */
  lastConnectedAtMs: number | null;
  /** Last observed WebSocket `CloseEvent.code` (1000 on clean stop, 1006 on TCP blip, etc.). */
  lastCloseCode: number | null;
  /** Last observed WebSocket `CloseEvent.reason` (often empty in practice). */
  lastCloseReason: string;
  /** Current number of failed reconnect attempts in the active streak. Resets to 0 on first Results frame after a reconnect. */
  reconnectAttempts: number;
  /** Cumulative successful reconnects across the lifetime of the session. */
  totalReconnectsSucceeded: number;
  /** Total bytes sent over the WS since session start (cumulative). */
  bytesSent: number;
  /** Total Int16 PCM frames sent since session start (cumulative). */
  framesSent: number;
  /** PCM frames dropped due to `bufferedAmount` backpressure (Phase 2). */
  droppedFrames: number;
  /** `{type:"KeepAlive"}` JSON frames sent during upstream silence (Phase 2). */
  keepalivesSent: number;
  /** ms timestamp of the most recent inbound `Results` frame, or null if none yet. */
  lastResultAtMs: number | null;
  /** `navigator.connection.effectiveType` captured at session start. May be null on browsers without the API. */
  effectiveType: string | null;
  /** PCM sample rate (Hz) locked at session start from effectiveType. Phase 3 adaptive rate. */
  pcmSampleRateHz: number | null;
  /** Whether resample+encode runs in pcmResample.worklet or on the main thread. */
  audioPath: DeepgramAudioPath | null;
};

export function emptyDiagnostics(): DeepgramDiagnostics {
  return {
    sessionStartedAtMs: null,
    lastConnectAttemptAtMs: null,
    lastConnectedAtMs: null,
    lastCloseCode: null,
    lastCloseReason: "",
    reconnectAttempts: 0,
    totalReconnectsSucceeded: 0,
    bytesSent: 0,
    framesSent: 0,
    droppedFrames: 0,
    keepalivesSent: 0,
    lastResultAtMs: null,
    effectiveType: null,
    pcmSampleRateHz: null,
    audioPath: null,
  };
}

/**
 * Reads `navigator.connection.effectiveType` defensively. Not all
 * browsers expose `NetworkInformation`; Safari doesn't, for example.
 * Returns null when unavailable so the diagnostics payload stays
 * truthful instead of pretending we know.
 */
export function readEffectiveType(): string | null {
  if (typeof navigator === "undefined") return null;
  const nav = navigator as unknown as { connection?: { effectiveType?: string } };
  const t = nav.connection?.effectiveType;
  return typeof t === "string" && t.length > 0 ? t : null;
}

export type UnexpectedCloseLog = {
  code: number;
  reason: string;
  effectiveType: string | null;
  bytesSentSession: number;
  framesSentSession: number;
  msSinceLastConnected: number | null;
  msSinceLastResult: number | null;
  reconnectAttempts: number;
};

/**
 * Build the Sentry-shaped payload for an unexpected WS close. Pure so
 * the structure is testable; the engine wraps this with a `console.warn`
 * call so DevTools surfaces it in the field. Once we wire real Sentry
 * (post-v1.0), the engine can swap the console.warn for `Sentry.captureMessage`
 * without touching this builder.
 */
export function buildUnexpectedCloseLog(args: {
  code: number;
  reason: string | undefined | null;
  diagnostics: DeepgramDiagnostics;
  nowMs: number;
}): UnexpectedCloseLog {
  const { code, reason, diagnostics, nowMs } = args;
  const msSinceLastConnected =
    diagnostics.lastConnectedAtMs != null ? Math.max(0, nowMs - diagnostics.lastConnectedAtMs) : null;
  const msSinceLastResult =
    diagnostics.lastResultAtMs != null ? Math.max(0, nowMs - diagnostics.lastResultAtMs) : null;
  return {
    code,
    reason: typeof reason === "string" ? reason : "",
    effectiveType: diagnostics.effectiveType,
    bytesSentSession: diagnostics.bytesSent,
    framesSentSession: diagnostics.framesSent,
    msSinceLastConnected,
    msSinceLastResult,
    reconnectAttempts: diagnostics.reconnectAttempts,
  };
}

// --- platform_dev diagnostics export (§4 Q4) — no transcript text ---

/** Rolling window for "Copy diagnostics" (~5 min at 1 Hz when panel is open). */
export const DEEPGRAM_DIAGNOSTICS_CAPTURE_WINDOW_MS = 5 * 60 * 1000;

/** Max retained snapshots (~5 min at 1 Hz + headroom). */
export const DEEPGRAM_DIAGNOSTICS_RING_CAPACITY = 320;

export type DeepgramDiagnosticsSnapshot = {
  capturedAtMs: number;
  diagnostics: DeepgramDiagnostics;
};

export type DeepgramUnexpectedCloseRecord = UnexpectedCloseLog & {
  capturedAtMs: number;
};

/**
 * Sentry-ready export blob for platform_dev "Copy diagnostics".
 * - `diagnosticsSnapshots`: 1 Hz samples while the dev panel is open.
 * - `unexpectedCloses`: same shape as `buildUnexpectedCloseLog` + `capturedAtMs`.
 * - `current`: point-in-time `getDiagnostics()` at export (no transcript).
 */
export type DeepgramDiagnosticsExport = {
  schemaVersion: 1;
  exportedAtMs: number;
  captureWindowMs: number;
  diagnosticsSnapshots: DeepgramDiagnosticsSnapshot[];
  unexpectedCloses: DeepgramUnexpectedCloseRecord[];
  current: DeepgramDiagnostics | null;
};

export class DeepgramDiagnosticsRingBuffer {
  private snapshots: DeepgramDiagnosticsSnapshot[] = [];
  private unexpectedCloses: DeepgramUnexpectedCloseRecord[] = [];

  pushSnapshot(diagnostics: DeepgramDiagnostics, nowMs = Date.now()): void {
    this.snapshots.push({ capturedAtMs: nowMs, diagnostics: { ...diagnostics } });
    this.prune(nowMs);
  }

  pushUnexpectedClose(payload: UnexpectedCloseLog, nowMs = Date.now()): void {
    this.unexpectedCloses.push({ capturedAtMs: nowMs, ...payload });
    this.prune(nowMs);
  }

  buildExport(current: DeepgramDiagnostics | null, nowMs = Date.now()): DeepgramDiagnosticsExport {
    this.prune(nowMs);
    return {
      schemaVersion: 1,
      exportedAtMs: nowMs,
      captureWindowMs: DEEPGRAM_DIAGNOSTICS_CAPTURE_WINDOW_MS,
      diagnosticsSnapshots: this.snapshots.map((s) => ({
        capturedAtMs: s.capturedAtMs,
        diagnostics: { ...s.diagnostics },
      })),
      unexpectedCloses: this.unexpectedCloses.map((e) => ({ ...e })),
      current: current ? { ...current } : null,
    };
  }

  private prune(nowMs: number): void {
    const cutoff = nowMs - DEEPGRAM_DIAGNOSTICS_CAPTURE_WINDOW_MS;
    this.snapshots = this.snapshots.filter((s) => s.capturedAtMs >= cutoff);
    this.unexpectedCloses = this.unexpectedCloses.filter((e) => e.capturedAtMs >= cutoff);
    if (this.snapshots.length > DEEPGRAM_DIAGNOSTICS_RING_CAPACITY) {
      this.snapshots = this.snapshots.slice(-DEEPGRAM_DIAGNOSTICS_RING_CAPACITY);
    }
    if (this.unexpectedCloses.length > 50) {
      this.unexpectedCloses = this.unexpectedCloses.slice(-50);
    }
  }
}

let devDiagnosticsRing: DeepgramDiagnosticsRingBuffer | null = null;

export function getDeepgramDiagnosticsRingBuffer(): DeepgramDiagnosticsRingBuffer {
  if (!devDiagnosticsRing) devDiagnosticsRing = new DeepgramDiagnosticsRingBuffer();
  return devDiagnosticsRing;
}

/** @internal Vitest-only — fresh singleton between tests. */
export function resetDeepgramDiagnosticsRingBufferForTests(): void {
  devDiagnosticsRing = null;
}

/** Called from the streaming engine on `[deepgram] unexpected close`. */
export function recordDeepgramUnexpectedClose(args: {
  payload: UnexpectedCloseLog;
  diagnostics: DeepgramDiagnostics;
  nowMs?: number;
}): void {
  const nowMs = args.nowMs ?? Date.now();
  const ring = getDeepgramDiagnosticsRingBuffer();
  ring.pushUnexpectedClose(args.payload, nowMs);
  ring.pushSnapshot(args.diagnostics, nowMs);
}
